/**
 * Thin fetch wrapper for the Torvanis Money API.
 *
 * Normalises the two failure modes a user can actually hit — the API is
 * unreachable, or the API answered with the standard `{ code, message }`
 * envelope — into one error type carrying a sentence a person can act on.
 * Raw stack traces and status codes never reach the screen.
 */

import type { ErrorResponseBody } from '@torvanis/shared';

/** Empty in development (Vite proxies to the API) and set by Helm in production. */
const configuredBaseUrl: string = import.meta.env.VITE_API_BASE_URL ?? '';

export const apiBaseUrl = configuredBaseUrl.replace(/\/+$/, '');

export class ApiRequestError extends Error {
  readonly code: string;
  readonly status: number | undefined;
  readonly details: Record<string, unknown> | undefined;

  constructor(
    message: string,
    options: { code: string; status?: number; details?: Record<string, unknown> },
  ) {
    super(message);
    this.name = 'ApiRequestError';
    this.code = options.code;
    this.status = options.status;
    this.details = options.details;
  }
}

function isErrorResponseBody(value: unknown): value is ErrorResponseBody {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { code?: unknown }).code === 'string' &&
    typeof (value as { message?: unknown }).message === 'string'
  );
}

export interface ApiRequestOptions {
  signal?: AbortSignal;
  /** Milliseconds before the request is abandoned and reported as unreachable. */
  timeoutMs?: number;
  /**
   * Extra status codes whose body is still a valid document rather than an
   * error — `/readyz` answers 503 with a full dependency report, for example.
   */
  acceptStatuses?: readonly number[];
}

const DEFAULT_TIMEOUT_MS = 10_000;

type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

interface ApiRequestInit extends ApiRequestOptions {
  method: HttpMethod;
  /** Serialised as JSON when present. */
  body?: unknown;
}

/**
 * One request path for every verb so the error envelope, the credential mode,
 * and the timeout behave identically everywhere.
 */
async function apiRequest<TResponse>(
  path: string,
  options: ApiRequestInit,
): Promise<TResponse | undefined> {
  const controller = new AbortController();
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);

  options.signal?.addEventListener('abort', () => controller.abort(), { once: true });

  const headers: Record<string, string> = { accept: 'application/json' };
  if (options.body !== undefined) {
    headers['content-type'] = 'application/json';
  }

  let response: Response;
  try {
    response = await fetch(`${apiBaseUrl}${path}`, {
      method: options.method,
      headers,
      credentials: 'include',
      signal: controller.signal,
      ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
    });
  } catch {
    throw new ApiRequestError(
      "We couldn't reach the Torvanis Money service. Check your connection and try again.",
      { code: 'NETWORK_UNREACHABLE' },
    );
  } finally {
    window.clearTimeout(timer);
  }

  const payload: unknown =
    response.status === 204 ? undefined : await response.json().catch(() => undefined);

  const accepted = response.ok || (options.acceptStatuses?.includes(response.status) ?? false);

  if (!accepted) {
    if (isErrorResponseBody(payload)) {
      throw new ApiRequestError(payload.message, {
        code: payload.code,
        status: response.status,
        ...(payload.details === undefined ? {} : { details: payload.details }),
      });
    }
    throw new ApiRequestError(
      `The service responded with an unexpected error (HTTP ${String(response.status)}).`,
      { code: 'UNEXPECTED_RESPONSE', status: response.status },
    );
  }

  if (payload === undefined && response.status !== 204) {
    throw new ApiRequestError('The service returned a response we could not read.', {
      code: 'UNREADABLE_RESPONSE',
      status: response.status,
    });
  }

  return payload as TResponse | undefined;
}

/** `GET` a JSON document, or throw an {@link ApiRequestError} explaining why not. */
export async function apiGet<TResponse>(
  path: string,
  options: ApiRequestOptions = {},
): Promise<TResponse> {
  return (await apiRequest<TResponse>(path, { ...options, method: 'GET' })) as TResponse;
}

export async function apiPost<TResponse>(
  path: string,
  body?: unknown,
  options: ApiRequestOptions = {},
): Promise<TResponse> {
  return (await apiRequest<TResponse>(path, {
    ...options,
    method: 'POST',
    ...(body === undefined ? {} : { body }),
  })) as TResponse;
}

/** `PUT` a JSON body — used where the body *is* the whole resource. */
export async function apiPut<TResponse>(
  path: string,
  body: unknown,
  options: ApiRequestOptions = {},
): Promise<TResponse> {
  return (await apiRequest<TResponse>(path, { ...options, method: 'PUT', body })) as TResponse;
}

export async function apiPatch<TResponse>(
  path: string,
  body: unknown,
  options: ApiRequestOptions = {},
): Promise<TResponse> {
  return (await apiRequest<TResponse>(path, { ...options, method: 'PATCH', body })) as TResponse;
}

export async function apiDelete(path: string, options: ApiRequestOptions = {}): Promise<void> {
  await apiRequest<undefined>(path, { ...options, method: 'DELETE' });
}
